/* eslint-disable @typescript-eslint/no-unsafe-type-assertion, @typescript-eslint/no-unsafe-argument, one-var, sonarjs/cognitive-complexity */
// oxlint-disable complexity,e max-lines-per-function, max-statements, typescript/no-explicit-any, no-plusplus, max-params
// Optimized deep-equal - targeting modern browsers + Node.js
// TypeScript version kept structurally identical to optimized.js on the hot path.

const hasOwn = Object.prototype.hasOwnProperty;
const valOf = Object.prototype.valueOf;
const toStr = Object.prototype.toString;

type TypedArrayLike = ArrayLike<unknown>;

function customToStringState(a: any, b: any): -1 | 0 | 1 {
  const toStringA = a.toString;
  const toStringB = b.toString;
  const hasCustomA = typeof toStringA === 'function' && toStringA !== toStr;
  const hasCustomB = typeof toStringB === 'function' && toStringB !== toStr;
  if (!hasCustomA && !hasCustomB) {
    return 0;
  }

  return typeof toStringA === 'function' && typeof toStringB === 'function'
    ? 1
    : -1;
}

function customValueOfState(a: any, b: any): -1 | 0 | 1 {
  const valueOfA = a.valueOf;
  const valueOfB = b.valueOf;
  const hasCustomA = typeof valueOfA === 'function' && valueOfA !== valOf;
  const hasCustomB = typeof valueOfB === 'function' && valueOfB !== valOf;
  if (!hasCustomA && !hasCustomB) {
    return 0;
  }

  return typeof valueOfA === 'function' && typeof valueOfB === 'function'
    ? 1
    : -1;
}

function equal(a: unknown, b: unknown): boolean;
function equal(a: any, b: any): boolean {
  if (a === b) {
    return true;
  }

  if (a && b && typeof a === 'object' && typeof b === 'object') {
    const ctor = a.constructor;
    if (ctor !== b.constructor) {
      return false;
    }

    let i: number, length: number;

    // Plain Object — most common in JSON/config/state data
    if (ctor === Object) {
      if (a.valueOf !== valOf || b.valueOf !== valOf) {
        const methodState = customValueOfState(a, b);
        if (methodState < 0) {
          return false;
        }
        if (methodState > 0) {
          return a.valueOf() === b.valueOf();
        }
      }
      if (a.toString !== toStr || b.toString !== toStr) {
        const methodState = customToStringState(a, b);
        if (methodState < 0) {
          return false;
        }
        if (methodState > 0) {
          return a.toString() === b.toString();
        }
      }

      const keys = Object.keys(a);
      ({ length } = keys);
      if (length !== Object.keys(b).length) {
        return false;
      }

      for (i = 0; i < length; i++) {
        const key = keys[i];
        if (!key) {
          continue;
        }

        if (!hasOwn.call(b, key)) {
          return false;
        }
        const value = a[key];
        const otherValue = b[key];
        if (value !== otherValue && !equal(value, otherValue)) {
          return false;
        }
      }

      return true;
    }

    // Array
    if (ctor === Array) {
      ({ length } = a);
      if (length !== b.length) {
        return false;
      }
      for (i = 0; i < length; i++) {
        const value = a[i];
        const otherValue = b[i];
        if (value !== otherValue && !equal(value, otherValue)) {
          return false;
        }
      }

      return true;
    }

    // Map — single pass
    if (ctor === Map) {
      if (a.size !== b.size) {
        return false;
      }
      for (const [key, value] of a) {
        if (!b.has(key)) {
          return false;
        }
        const otherValue = b.get(key);
        if (value !== otherValue && !equal(value, otherValue)) {
          return false;
        }
      }

      return true;
    }

    // Set
    if (ctor === Set) {
      if (a.size !== b.size) {
        return false;
      }
      for (const val of a) {
        if (!b.has(val)) {
          return false;
        }
      }

      return true;
    }

    // RegExp
    if (ctor === RegExp) {
      return a.source === b.source && a.flags === b.flags;
    }

    // Date
    if (ctor === Date) {
      return a.getTime() === b.getTime();
    }

    if (ctor === ArrayBuffer) {
      length = a.byteLength;
      if (length !== b.byteLength) {
        return false;
      }

      return equalBytes(a, 0, b, 0, length);
    }

    // TypedArrays
    if (ArrayBuffer.isView(a)) {
      if (ctor === DataView) {
        length = a.byteLength;
        if (length !== b.byteLength) {
          return false;
        }

        return equalBytes(
          a.buffer,
          a.byteOffset,
          b.buffer,
          b.byteOffset,
          length,
        );
      }

      const viewA = a as unknown as TypedArrayLike;
      const viewB = b as unknown as TypedArrayLike;
      ({ length } = viewA);
      if (length !== viewB.length) {
        return false;
      }
      for (i = 0; i < length; i++) {
        if (viewA[i] !== viewB[i]) {
          return false;
        }
      }

      return true;
    }

    // Fallback for custom objects
    if (a.valueOf !== valOf || b.valueOf !== valOf) {
      const methodState = customValueOfState(a, b);
      if (methodState < 0) {
        return false;
      }
      if (methodState > 0) {
        return a.valueOf() === b.valueOf();
      }
    }

    if (a.toString !== toStr || b.toString !== toStr) {
      const methodState = customToStringState(a, b);
      if (methodState < 0) {
        return false;
      }
      if (methodState > 0) {
        return a.toString() === b.toString();
      }
    }

    const keys = Object.keys(a);
    ({ length } = keys);
    if (length !== Object.keys(b).length) {
      return false;
    }
    for (i = 0; i < length; i++) {
      const key = keys[i];
      if (!key) {
        continue;
      }

      if (!hasOwn.call(b, key)) {
        return false;
      }
      const value = a[key];
      const otherValue = b[key];
      if (value !== otherValue && !equal(value, otherValue)) {
        return false;
      }
    }

    return true;
  }

  // true if both NaN, false otherwise
  // oxlint-disable-next-line no-self-compare
  return a !== a && b !== b;
}
function equalBytes(
  bufferA: ArrayBufferLike,
  offsetA: number,
  bufferB: ArrayBufferLike,
  offsetB: number,
  length: number,
): boolean {
  const bytesA = new Uint8Array(bufferA, offsetA, length);
  const bytesB = new Uint8Array(bufferB, offsetB, length);

  for (let index = 0; index < length; index++) {
    if (bytesA[index] !== bytesB[index]) {
      return false;
    }
  }

  return true;
}

export { equal };
